import { DeleteFilled } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Divider, InputNumber, Modal, Space, Table, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { EntitySelectorRef } from "components/EntitySelector";
import PackageSelector from "components/PackageSelector";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { StaffPackage } from "models/packages";
import { DateOnlyFormat } from "models/websiteSettings";
import moment from "moment-timezone";
import { FC, useRef, useState } from "react";

const { Text } = Typography;

interface MoreListingsTonightModalProps {
    open: boolean;
    currentUploadCount: number;
    onCancel?: () => void;
    onConfirm?: (uploadDate: string, packageIds: string[]) => Promise<void>;
}

interface PendingCounts {
    cardCount: number;
    newUploadDate: string;
    packageIds: string[];
}
interface PendingPackage {
    id: string;
    friendlyId: number;
    customerName: string;
    count: number;
}

const usePotentialUploads = () => {
    const fetch = useAuthenticatedRequestCreator<PendingCounts, number>(
        (estimate) => ({
            method: "get",
            url: `/pending-listings/potential-uploads?cardCount=${estimate}`
        })
    )

    const { mutateAsync: potentialUploads, isLoading } = useMutation(fetch);

    return {
        potentialUploads,
        isLoading
    };
}

const usePendingPackageUpload = () => {
    const fetch = useAuthenticatedRequestCreator<PendingPackage, number>(
        (id) => ({
            method: "get",
            url: `/pending-listings/pending-package?friendlyId=${id}`
        }), [404]
    );

    const { mutateAsync: pendingPackage, isLoading } = useMutation(fetch);

    return { pendingPackage, isLoading };
}

const MoreListingsTonightModal: FC<MoreListingsTonightModalProps> = ({
    open, currentUploadCount, onConfirm, onCancel
}) => {
    const { potentialUploads, isLoading } = usePotentialUploads();
    const { pendingPackage, isLoading: isFetchingPendingPkg } = usePendingPackageUpload();
    const [estimate, setEstimate] = useState<number | null>(null);
    const [counts, setCounts] = useState<PendingCounts | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [selectedPkg, setSelectedPkg] = useState<StaffPackage | undefined>();
    const [pendingPkgs, setPendingPkgs] = useState<PendingPackage[]>([]);
    const [pkgError, setPkgError] = useState<number | undefined>();
    const ref = useRef<EntitySelectorRef>(null);

    const working = confirming || isLoading || isFetchingPendingPkg;

    const check = async () => {
        const result = await potentialUploads(estimate!);
        setCounts(result);
    }

    const reset = () => {
        setEstimate(null);
        setCounts(null);
        setSelectedPkg(undefined);
        setPendingPkgs([]);
        setPkgError(undefined);
        ref.current?.reset();
        if (onCancel) {
            onCancel();
        }
    }

    const confirm = async () => {
        setConfirming(true);

        try {
            // 'unioning' the counts result with any specific package ids
            const uploadDate = counts?.newUploadDate ?? moment().local().format(DateOnlyFormat);
            const pkgIds = [...(counts?.packageIds ?? []), ...pendingPkgs.map(p => p.id)]

            if (onConfirm) {
                await onConfirm(uploadDate, pkgIds);
            }

            setEstimate(null);
            setCounts(null);
        } finally {
            setConfirming(false);
        }
    }

    const appendPackage = async () => {
        setPkgError(undefined);

        try {
            const pkg = await pendingPackage(selectedPkg!.friendlyId);
            setPendingPkgs(cur => [...cur, pkg]);
        } catch {
            setPkgError(selectedPkg!.friendlyId);
        } finally {
            ref.current?.reset();
            setSelectedPkg(undefined);
        }
    }

    const columns: ColumnsType<PendingPackage> = [
        {
            render: ((_, rec) => <Button
                danger icon={<DeleteFilled />}
                onClick={() => setPendingPkgs(cur => [...cur.filter(p => p.id !== rec.id)])} />)
        },
        { title: "Package ID", dataIndex: "friendlyId" },
        { title: "Customer", dataIndex: "customerName" },
        { title: "Count", dataIndex: "count", align: 'right' }
    ];

    return <Modal
        title="Enqueue More Listings for Tonight"
        open={open}
        closable={false}
        keyboard={false}
        maskClosable={false}
        onOk={confirm}
        okButtonProps={{ disabled: (counts?.cardCount ?? 0) === 0 && pendingPkgs.length === 0 }}
        onCancel={reset}
        confirmLoading={working}
        destroyOnClose
    >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Text>{currentUploadCount} listings going up tonight, how many more would you like to add?</Text>
            <Space>
                <InputNumber value={estimate} onChange={setEstimate} min={1} />
                <Button onClick={check} disabled={!estimate}>Check</Button>
            </Space>
            {counts !== null && <Text>
                Found an additional {counts.cardCount.toLocaleString()} card(s) across {counts.packageIds.length.toLocaleString()} package(s),
                which would make for a total of <Text type="success" strong>{(currentUploadCount + counts.cardCount).toLocaleString()}</Text> listings going up tonight.
            </Text>}

            <Divider>And / Or</Divider>

            <Text>
                Add specific packages to be uploaded tonight
            </Text>
            <Space>
                <PackageSelector
                    ref={ref}
                    placeholder="Package ID"
                    onSelected={setSelectedPkg}
                    onClear={() => setSelectedPkg(undefined)}
                />
                <Button disabled={!selectedPkg || isLoading} onClick={appendPackage}>
                    Add
                </Button>
                {pkgError && <Text type="danger">{pkgError} has no pending uploads!</Text>}
            </Space>
            {pendingPkgs.length > 0 && <Table
                size="small"
                rowKey={r => r.id}
                columns={columns}
                dataSource={pendingPkgs}
                footer={() => <div style={{ display: 'flex', justifySelf: 'end', fontWeight: 'bold' }}>
                    {pendingPkgs.map(p => p.count).reduce((s, c) => s += c, 0)}
                </div>}
                pagination={false}
                loading={isLoading}
            />}
        </Space>
    </Modal>
}

export default MoreListingsTonightModal;